import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { GET_URL_NEW } from '@utils/api/endpoints';
import { BaseError } from '@utils/types/shared/base';
import { UsageBoResponse } from '@utils/types/back-office/usage-bo';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';

type UsageParams = {
  period_start?: string;
  period_end?: string;
  direction?: 'asc' | 'desc';
  sort?: string;
  language?: boolean;
};

const getDefaultDates = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { period_start: fmt(first), period_end: fmt(now) };
};

export const useGetUsageLegacyBo = (params: UsageParams) => {
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const { usageLegacyWith } = GET_URL_NEW({
    offset,
    limit,
    sort: params.sort,
    direction: params.direction
  });

  const baseUrl =
    typeof usageLegacyWith === 'function' ? usageLegacyWith() : usageLegacyWith;

  const fetchUsage = async () => {
    const splits = ['organization', 'month'];
    if (params.language) splits.push('language');

    // Always provide a complete range to the server to avoid 422 on clear.
    const defaults = getDefaultDates();
    const resolvedStart = params.period_start || defaults.period_start;
    const resolvedEnd = params.period_end || defaults.period_end;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const q: Record<string, any> = {
      period_start: resolvedStart,
      period_end: resolvedEnd
    };

    const url =
      baseUrl +
      (baseUrl.includes('?') ? '&' : '?') +
      splits.map(s => `splits=${encodeURIComponent(s)}`).join('&');

    const { data } = await Axios.get<UsageBoResponse>(url, { params: q });
    return data;
  };

  return useQuery<UsageBoResponse, AxiosError<BaseError>>(
    [
      'usageLegacy',
      offset,
      limit,
      params.period_start ?? null,
      params.period_end ?? null,
      params.language ?? false,
      params.sort ?? null,
      params.direction ?? null
    ],
    fetchUsage,
    {
      keepPreviousData: true,
      staleTime: 0,
      retry: 1,
      refetchOnWindowFocus: false,
      useErrorBoundary: false
    }
  );
};
